import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronDownIcon } from 'lucide-react';
import { findDegreesTemplates } from '../data/findDegreesTemplates';
function slugToLabel(slug: string): string {
  return slug.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
}
interface FooterLink {
  label: string;
  to: string;
}
const topPrograms: FooterLink[] = [{
  label: 'Business Administration',
  to: '/degrees/business'
}, {
  label: 'Computer Science',
  to: '/degrees/computer-science'
}, {
  label: 'Nursing & Healthcare',
  to: '/degrees/healthcare'
}, {
  label: 'Psychology',
  to: '/degrees/psychology'
}];
const resourceLinks: FooterLink[] = [{
  label: 'Partner Colleges',
  to: '/colleges'
}];
const guideLinks: FooterLink[] = [{
  label: 'Request Information',
  to: '/request-information'
}, {
  label: 'Online Christian Colleges',
  to: '/guide/online-christian-colleges'
}, {
  label: 'Financial Aid Guide',
  to: '#'
}, {
  label: 'Accreditation Info',
  to: '#'
}, {
  label: 'Career Paths',
  to: '#'
}, {
  label: 'Student Success Stories',
  to: '#'
}];
const legalLinks: FooterLink[] = [{
  label: 'Privacy Policy',
  to: '/privacy-policy'
}, {
  label: 'Terms of Service',
  to: '/terms-and-conditions'
}, {
  label: 'Cookie Policy',
  to: '/cookie-policy'
}, {
  label: 'Do Not Sell My Info',
  to: '/do-not-sell-my-info'
}];
const linkClasses = 'text-sm text-ink-secondary transition-colors duration-150 ease-out hover:text-primary focus:outline-none focus-visible:rounded focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2';
const columnHeadingClasses = 'text-sm font-semibold tracking-wide text-ink mb-4';
export function Footer() {
  const currentYear = new Date().getFullYear();
  const [isFindDegreesOpen, setIsFindDegreesOpen] = useState(false);
  const scrollToTop = () => window.scrollTo({
    top: 0,
    behavior: 'smooth'
  });
  return <footer className="w-full bg-surface border-t border-hairline" data-id="element-210" data-mp="yto5vsomlxsm">
      <div className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-8 py-14 lg:py-16" data-id="element-211" data-mp="yrv4arsve3rh">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-12 md:gap-8" data-id="element-212" data-mp="13rzfq3ex88au">
          {/* Brand */}
          <div data-id="element-213" data-mp="18lby3vt0biv5">
            <Link to="/" onClick={scrollToTop} className="flex items-center mb-4 focus:outline-none focus-visible:rounded focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2" data-id="element-214" data-mp="18lu2jfptp3e5">
              <img src="https://cdn.magicpatterns.com/uploads/98gXraqnTFT6vsk8BtTnBr/FOC_Logo_(1).png" alt="findonlinecollege.com" className="h-10 w-auto object-contain" data-id="element-215" data-mp="1ml3eg36zxbcb" />
            </Link>
            <p className="max-w-xs text-sm leading-relaxed text-ink-secondary" data-id="element-216" data-mp="3ta8c1ohfn9dm">
              Helping you find and compare accredited online degree programs
              that fit your goals, your budget, and your schedule.
            </p>
          </div>

          {/* Programs + Find Degrees */}
          <div data-id="element-217" data-mp="21mtik8w4oy2d">
            <nav aria-label="Top Programs" data-id="element-218" data-mp="21mab5m43ts2l">
              <h2 className={columnHeadingClasses} data-id="element-219" data-mp="21n1tvwclrvbg">Top Programs</h2>
              <ul className="space-y-3 mb-8" data-id="element-220" data-mp="1woi93z9qwpko">
                {topPrograms.map(link => <li key={link.label} data-id="element-221" data-mp="2te9e4sfnxv4q">
                    <Link to={link.to} onClick={scrollToTop} className={linkClasses} data-id="element-222" data-mp="2td6kicqyhel4">
                      {link.label}
                    </Link>
                  </li>)}
              </ul>
            </nav>

            <h2 className={columnHeadingClasses} data-id="element-223" data-mp="2fgb2vvwn2etg">Find Degrees</h2>
            <div className="relative" data-id="element-224" data-mp="18yxl31yqj24j">
              <button type="button" onClick={() => setIsFindDegreesOpen(open => !open)} aria-expanded={isFindDegreesOpen} aria-haspopup="listbox" className="w-full flex items-center justify-between gap-2 h-11 px-4 text-sm font-medium text-ink bg-background-alt border border-hairline rounded-xl transition-colors duration-150 ease-out hover:bg-surface focus:outline-none focus-visible:ring-2 focus-visible:ring-primary" data-id="element-225" data-mp="18vrypuhhqeci">
                <span data-id="element-226" data-mp="3bk01huke74hh">Select a theme</span>
                <ChevronDownIcon aria-hidden="true" className={`w-4 h-4 text-primary transition-transform duration-200 ease-out ${isFindDegreesOpen ? 'rotate-180' : ''}`} data-id="element-227" data-mp="3bk8cti0va1qk" />
              </button>

              {isFindDegreesOpen && <ul role="listbox" className="absolute z-20 left-0 right-0 mt-2 bg-surface border border-hairline rounded-xl shadow-ds-lg overflow-hidden" data-id="element-228" data-mp="36jkk8rwzpcu8">
                  {findDegreesTemplates.length === 0 && <li className="px-4 py-2.5 text-xs text-ink-muted italic" data-id="element-229" data-mp="1nlg19r4p22vh">
                      No themes available yet
                    </li>}
                  {findDegreesTemplates.map(template => <li key={template.slug} data-id="element-230" data-mp="1wlck3c4ek7im">
                      <Link to={`/find-degrees/${template.slug}`} onClick={() => {
                  setIsFindDegreesOpen(false);
                  scrollToTop();
                }} className="block px-4 py-2.5 text-sm text-ink-secondary transition-colors duration-150 ease-out hover:bg-background-alt hover:text-primary" data-id="element-231" data-mp="1wk9quxjlxszj">
                        {slugToLabel(template.slug)}
                      </Link>
                    </li>)}
                </ul>}
            </div>
          </div>

          {/* Resources + Guides */}
          <div data-id="element-232" data-mp="g8460k9cr18g">
            <nav aria-label="Resources" data-id="element-233" data-mp="g6b7js8w7nbj">
              <h2 className={columnHeadingClasses} data-id="element-234" data-mp="2gbf69jlvjwtp">Resources</h2>
              <ul className="space-y-3 mb-8" data-id="element-235" data-mp="2g8svlb3ot179">
                {resourceLinks.map(link => <li key={link.label} data-id="element-236" data-mp="2l7dpksscion9">
                    <Link to={link.to} onClick={scrollToTop} className={linkClasses} data-id="element-237" data-mp="2q06src861ox8">
                      {link.label}
                    </Link>
                  </li>)}
              </ul>
            </nav>

            <nav aria-label="Guides" data-id="element-238" data-mp="22bxecvda83g4">
              <h2 className={columnHeadingClasses} data-id="element-239" data-mp="22cyq7224gtyw">Guides</h2>
              <ul className="space-y-3" data-id="element-240" data-mp="2795wivyfk7hd">
                {guideLinks.map(link => <li key={link.label} data-id="element-241" data-mp="2bclq1jf7drpu">
                    <Link to={link.to} onClick={scrollToTop} className={linkClasses} data-id="element-242" data-mp="2b9fzdzqxehs0">
                      {link.label}
                    </Link>
                  </li>)}
              </ul>
            </nav>
          </div>

          {/* Legal */}
          <nav aria-label="Legal" data-id="element-243" data-mp="171iockr0h8fp">
            <h2 className={columnHeadingClasses} data-id="element-244" data-mp="172kapldh9hcq">Legal</h2>
            <ul className="space-y-3" data-id="element-245" data-mp="12a8zhmdae94v">
              {legalLinks.map(link => <li key={link.label} data-id="element-246" data-mp="xc7egvwavzqr">
                  <Link to={link.to} onClick={scrollToTop} className={linkClasses} data-id="element-247" data-mp="xb65qy2rbpc7">
                    {link.label}
                  </Link>
                </li>)}
            </ul>
          </nav>
        </div>

        <div className="mt-12 border-t border-hairline pt-8" data-id="element-248" data-mp="jczi0ariv5q9">
          <div className="text-xs leading-relaxed text-ink-muted space-y-4" data-id="element-249" data-mp="edmsfi99nkjz">
            <p data-id="element-250" data-mp="2dq7d83jetjcf">
              <strong className="font-semibold text-ink-secondary" data-id="element-251" data-mp="2dqqx3psmeht3">
                Advertiser Disclosure:
              </strong>{' '}
              OnlineCollegeGuide.com is an advertising-supported site. Featured
              or trusted partner programs and all school search, finder, or
              match results are for schools that compensate us. This
              compensation does not influence our school rankings, resource
              guides, or other editorially-independent information published on
              this site.
            </p>
            <p data-id="element-252" data-mp="1ct7h229g75fh">
              <strong className="font-semibold text-ink-secondary" data-id="element-253" data-mp="1cspb2dwzhcud">
                TCPA Disclosure:
              </strong>{' '}
              By submitting your information on this website, you consent to be
              contacted by our educational partners regarding their programs via
              email, telephone, or text message.
            </p>
            <p className="pt-2" data-id="element-254" data-mp="twz5kgpudybq">
              &copy; {currentYear} OnlineCollegeGuide. All rights reserved.
            </p>
          </div>
        </div>
      </div>
    </footer>;
}